<template>
  <form ref="companyForm" v-if="!pendingComponentInit" @submit.prevent="onSubmit">
    <div class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5"
        >{{ $t('mygwf.components.Form.name') }}: <span class="text-red-default">*</span></span
      >
      <base-input
        class="mmt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.name"
        type="text"
        :required="true"
        focused
      />
    </div>
    <div v-if="isVisible.sales_rep" class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.representative') }}:</span>
      <base-input
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.representative"
        type="text"
      />
    </div>
    <div v-if="isVisible.customer_account" class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.customer_account') }}:</span>
      <span class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3">
        {{ companyForm.customer_account }}
      </span>
    </div>
    <div v-if="isVisible.area_code" class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.area_code') }}:</span>
      <span class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3">
        {{ companyForm.area }}
      </span>
    </div>
    <div class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.address') }}:</span>
      <base-input
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.address"
        type="text"
      />
    </div>
    <div class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.city') }}:</span>
      <base-input
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.city"
        type="text"
      />
    </div>
    <div class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5"
        >{{ $t('mygwf.components.Form.country') }}: <span class="text-red-default">*</span></span
      >
      <base-select
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.country"
        :options="countryOptions"
        searchable
      />
    </div>
    <div class="py-3 xl:py-4 xl:flex items-center border-b">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.zip') }}:</span>
      <base-input
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.zip"
        type="text"
      />
    </div>
    <div v-if="mode === 'create'" class="py-3 xl:py-4 xl:flex items-center">
      <span class="xl:w-1/2 leading-5"
        >{{ $t('mygwf.components.Form.distributor') }}:
        <span class="text-red-default">*</span></span
      >
      <base-select
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.distributor"
        :options="distributorOptions"
      />
    </div>
    <div v-if="mode !== 'create'" class="py-3 xl:py-4 xl:flex items-center">
      <span class="xl:w-1/2 leading-5 flex flex-col gap-1"
        >{{ $t('mygwf.components.Form.mailing_list') }}:
        <span class="text-gray-400 font-light text-xs w-2/3">{{
          $t('mygwf.components.Form.mailing_list_tip')
        }}</span></span
      >
      <base-input
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.mailing_list"
        type="text"
      />
    </div>
    <div
      v-if="mode === 'edit' && $store.getters['auth/isStaff']"
      class="py-3 xl:py-4 xl:flex items-center"
    >
      <span class="xl:w-1/2 leading-5"
        >{{ $t('mygwf.components.Form.services') }}: <span class="text-red-default">*</span></span
      >
      <base-select
        class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3"
        v-model="companyForm.services"
        :options="serviceOptions"
        mode="multiple"
      />
    </div>
    <div v-else class="py-3 xl:py-4 xl:flex items-center">
      <span class="xl:w-1/2 leading-5">{{ $t('mygwf.components.Form.services') }}:</span>
      <span class="mt-3 leading-5 sm:mt-2 sm:col-span-2 space-x-4 w-full relative pb-2 lg:pb-3">{{
        generateServicesText(companyForm.services)
      }}</span>
    </div>

    <!-- Action buttons -->
    <div class="flex justify-end space-x-3 pt-5 my-1 border-t">
      <span class="self-center mr-auto"
        ><span class="text-red-default">*</span> {{ requiredFieldText }}</span
      >
      <base-button @click="$emit('close')" action="close" context="secondary">
        <base-icon icon="close" />
      </base-button>
      <base-button
        type="submit"
        action="save"
        :loading="pendingRequest"
        :disabled="$v.companyForm.$invalid"
      >
        <base-icon icon="success" />
      </base-button>
    </div>
  </form>
</template>

<script>
import {BaseInput, BaseButton, BaseIcon, BaseSelect} from '@/components/base'
import ListSelect from '@/components/shared/ListSelect'
import cloneDeep from 'lodash-es/cloneDeep'
import {required, requiredIf} from 'vuelidate/lib/validators'
import {getData} from 'country-list'

export default {
  components: {
    BaseInput,
    BaseSelect,
    BaseButton,
    BaseIcon,
    ListSelect,
  },

  props: {
    mode: {
      type: String,
      required: true,
    },
    companyId: {
      type: Number,
      default: -1,
    },
  },

  data() {
    return {
      pendingComponentInit: false,
      pendingRequest: false,
      errors: {},
      countryOptions: [],
      serviceOptions: [],
      companyForm: {
        name: '',
        representative: '',
        customer_account: '',
        area: '',
        address: '',
        city: '',
        country: null,
        zip: '',
        mailing_list: '',
        distributor: null,
        services: [],
      },
    }
  },

  computed: {
    requiredFieldText() {
      return this.$t('mygwf.components.Form.requiredField')
    },
    distributorOptions() {
      if (this.mode !== 'create') {
        return []
      }
      return this.$store.getters['companies/list']
        .filter(company => company.is_distributor)
        .map(company => {
          return {label: company.name, id: company.id}
        })
    },
    isVisible() {
      const r = {
        area_code: false,
        customer_account: false,
        sales_rep: false,
      }
      // Create mode
      if (this.mode === 'create') {
        return r
      }

      // Edit mode
      // Distributor's company
      if (!this.companyForm.is_distributor && !this.companyForm.customer_account) {
        return r
      }

      // GWF distributor | GWF end customer
      r.area_code = true
      r.customer_account = true
      r.sales_rep = true

      return r
    },
  },

  watch: {
    distributorOptions(options) {
      if (options.length === 1) {
        this.companyForm.distributor = options[0].id
      }
    },
  },

  created() {
    this._initComponent()
  },

  methods: {
    // ================================================================================
    // Events
    // ================================================================================

    async onSubmit() {
      if (this.pendingRequest || this.$v.companyForm.$invalid) {
        return
      }

      const isInCreationMode = this.mode === 'create'
      try {
        this.pendingRequest = true
        await this.$store.dispatch(
          `companies/${isInCreationMode ? 'create' : 'update'}`,
          this.companyForm
        )
        this.$emit(isInCreationMode ? 'created' : 'updated', this.companyForm)
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: isInCreationMode ? 'created' : 'updated',
        })
      } catch (errors) {
        await this.$store.dispatch('app/notification', {
          type: 'error',
          group: 'app',
          text: isInCreationMode ? 'created' : 'updated',
        })
        this.errors = errors
      } finally {
        this.pendingRequest = false
      }
    },

    // ================================================================================
    // Helpers
    // ================================================================================

    _initComponent() {
      this.pendingComponentInit = true
      this._populateOptions()
      if (this.mode === 'edit') {
        const company = cloneDeep(
          this.$store.getters['companies/list'].find(c => c.id === this.companyId)
        )
        if (!company) {
          this.$emit('close')
        }
        this.companyForm = company
        if (this.companyForm.services && !this.companyForm.services.includes(1)) {
          this.companyForm.services.push(1)
        }
      }
      this.pendingComponentInit = false
    },

    _populateOptions() {
      // Countries
      this.countryOptions = getData().map(country => {
        return {label: country.name, id: country.code}
      })

      // Services
      if (this.mode === 'edit') {
        this.serviceOptions = this.$store.getters['auth/services'].map(s => {
          return {label: s.name, id: s.id, disabled: !s.editable}
        })
      }
    },

    generateServicesText(companyServices) {
      if (!companyServices || companyServices.length === 0) {
        return '-'
      }

      const services = this.$store.getters['auth/services']
      return companyServices
        .map(id => services.find(s => s.id === id))
        .map(s => s?.name || '-')
        .join(', ')
    },
  },

  // ================================================================================
  // Validation
  // ================================================================================
  validations: {
    companyForm: {
      name: {required},
      distributor: {
        required: requiredIf(function () {
          return this.mode === 'create'
        }),
      },
      country: {
        required: requiredIf(function () {
          return this.mode === 'create'
        }),
      },
      services: {
        required: requiredIf(function () {
          return this.mode === 'edit'
        }),
      },
    },
  },
}
</script>
